import {
    IServerFunnelResponse,
    IClientFunnelData,
    IClientFunnelStep,
    IJobLabelOption,
    IRecruiterOption,
    IServerTimeToHireResponse,
    IServerTimeToHireCompanyResponse,
    IServerTimeToHireJobResponse,
    IServerTimeToHireComparisonResponse,
    IClientTimeToHireData,
    IClientMetricData,
    IClientCurrentStatusMetric,
    IMetricData,
    ICurrentStatusMetric,
    JobStatus,
    IServerSourceOfHireStatsResponse,
    IClientSourceOfHireStats,
    IServerSourceOfHireChartResponse,
    IClientSourceOfHireChartData,
    IServerSourceOfHireEntry,
    IClientSourceOfHireEntry,
    IServerSourceOfHireNamedStat,
    IClientSourceOfHireNamedStat,
} from '../../../common';

export const formatFunnelResponse = (
    data: IServerFunnelResponse
): IClientFunnelData => {
    const base: IClientFunnelData = {
        view: data.view,
        total: data.total,
        dateRange: data.date_range
            ? {
                  fromDate: data.date_range.from_date,
                  toDate: data.date_range.to_date,
              }
            : undefined,
        funnel: [],
    };

    if (data.view === 'company') {
        return {
            ...base,
            funnel: data.funnel.map((item) => ({
                label: item.label,
                count: item.count,
                percentage: item.percentage,
            })),
        };
    }

    if (data.view === 'recruiter') {
        return {
            ...base,
            recruiter: {
                id: data.recruiter.id,
                firstName: data.recruiter.first_name,
                lastName: data.recruiter.last_name,
            },
            funnel: data.funnel.map((item) => ({
                label: item.label,
                count: item.count,
                percentage: item.percentage,
            })),
        };
    }

    const steps: IClientFunnelStep[] = data.steps.map((step) => ({
        name: step.name,
        stepIndex: step.step_index,
        inProgress: step.in_progress,
        accepted: step.accepted,
        rejected: step.rejected,
        withdrawn: step.withdrawn,
    }));

    const summary = data.summary;
    const inProgressTotal = steps.reduce(
        (sum, step) => sum + step.inProgress,
        0
    );

    return {
        ...base,
        job: { id: data.job.id, title: data.job.title },
        steps,
        summary,
        funnel: [
            { label: 'New', count: summary.new, percentage: 0 },
            {
                label: 'In Progress',
                count: inProgressTotal,
                percentage: 0,
            },
            { label: 'Hired', count: summary.hired, percentage: 0 },
            {
                label: 'Rejected',
                count: summary.rejected,
                percentage: 0,
            },
            {
                label: 'Withdrawn',
                count: summary.withdrawn,
                percentage: 0,
            },
        ],
    };
};

const formatMetric = (metric: IMetricData): IClientMetricData => ({
    averageDays: metric.average_days,
    count: metric.count,
});

const formatCurrentStatus = (
    metric: ICurrentStatusMetric
): IClientCurrentStatusMetric => ({
    count: metric.count,
    averageDays: metric.average_days,
    oldestDays: metric.oldest_days,
});

const isCompanyResponse = (
    data: IServerTimeToHireResponse
): data is IServerTimeToHireCompanyResponse =>
    'currently_in_new' in data && !('jobs' in data);

const isJobResponse = (
    data: IServerTimeToHireResponse
): data is IServerTimeToHireJobResponse => 'job_id' in data;

const isComparisonResponse = (
    data: IServerTimeToHireResponse
): data is IServerTimeToHireComparisonResponse => 'jobs' in data;

export const formatTimeToHireResponse = (
    data: IServerTimeToHireResponse
): IClientTimeToHireData => {
    if (isComparisonResponse(data)) {
        return {
            view: 'comparison',
            timeFrame: data.time_frame,
            startDate: data.start_date,
            endDate: data.end_date,
            jobs: data.jobs.map((job) => ({
                jobId: job.job_id,
                jobTitle: job.job_title,
                unprocessedToDeclined: formatMetric(job.unprocessed_to_declined),
                unprocessedToInReview: formatMetric(job.unprocessed_to_in_review),
                timeInReview: formatMetric(job.time_in_review),
                hiringTimeAverage: formatMetric(job.hiring_time_average),
                timeToFirstApplication: formatMetric(job.time_to_first_application),
                currentlyInNew: formatCurrentStatus(job.currently_in_new),
                currentlyInReview: formatCurrentStatus(job.currently_in_review),
            })),
        };
    }

    if (isJobResponse(data)) {
        return {
            view: 'job',
            jobId: data.job_id,
            jobTitle: data.job_title,
            timeFrame: data.time_frame,
            startDate: data.start_date,
            endDate: data.end_date,
            unprocessedToDeclined: formatMetric(data.unprocessed_to_declined),
            unprocessedToInReview: formatMetric(data.unprocessed_to_in_review),
            timeInReview: formatMetric(data.time_in_review),
            hiringTimeAverage: formatMetric(data.hiring_time_average),
            timeToFirstApplication: formatMetric(data.time_to_first_application),
            timePerStep: {
                steps: data.time_per_step.steps.map((step) => ({
                    stepId: step.step_id,
                    stepName: step.step_name,
                    averageDays: step.average_days,
                    averageHours: step.average_hours,
                    averageMinutes: step.average_minutes,
                    count: step.count,
                })),
                totalAverageDays: data.time_per_step.total_average_days,
            },
        };
    }

    if (isCompanyResponse(data)) {
        return {
            view: 'company',
            timeFrame: data.time_frame,
            startDate: data.start_date,
            endDate: data.end_date,
            unprocessedToDeclined: formatMetric(data.unprocessed_to_declined),
            unprocessedToInReview: formatMetric(data.unprocessed_to_in_review),
            timeInReview: formatMetric(data.time_in_review),
            hiringTimeAverage: formatMetric(data.hiring_time_average),
            timeToFirstApplication: formatMetric(data.time_to_first_application),
            currentlyInNew: formatCurrentStatus(data.currently_in_new),
            currentlyInReview: formatCurrentStatus(data.currently_in_review),
        };
    }

    throw new Error('Unknown TimeToHire response format');
};

const formatSourceOfHireEntries = (
    entries: IServerSourceOfHireEntry[]
): IClientSourceOfHireEntry[] =>
    entries.map((entry) => ({
        sourceTitle: entry.source_title,
        amount: entry.amount,
    }));

export const formatSourceOfHireStatsResponse = (
    data: IServerSourceOfHireStatsResponse
): IClientSourceOfHireStats => ({
    topThreeClicks: formatSourceOfHireEntries(data.top_three_clicks),
    topThreeApplicants: formatSourceOfHireEntries(data.top_three_applicants),
    topThreeHired: formatSourceOfHireEntries(data.top_three_hired),
    topThreeSuccessRate: formatSourceOfHireEntries(data.top_three_success_rate),
});

const formatSourceOfHireNamedStat = (
    stat: IServerSourceOfHireNamedStat
): IClientSourceOfHireNamedStat => ({
    sourceTitle: stat.source_title,
    total: Number(stat.total),
    totalPercent: stat.total_percent,
});

export const formatSourceOfHireChartResponse = (
    data: IServerSourceOfHireChartResponse
): IClientSourceOfHireChartData => ({
    clicks: formatSourceOfHireEntries(data.clicks),
    applications: formatSourceOfHireEntries(data.applications),
    screened: formatSourceOfHireEntries(data.screened),
    hired: formatSourceOfHireEntries(data.hired),
    clickApplyPercent: formatSourceOfHireEntries(data.click_apply_percent),
    applyScreenPercent: formatSourceOfHireEntries(data.apply_screen_percent),
    overallConversion: formatSourceOfHireEntries(data.overall_conversion),
    topStatistics: {
        totalHires: Number(data.top_statistics.total_hires.total),
        percentChange: data.top_statistics.total_hires.percent_change,
        topSource: formatSourceOfHireNamedStat(data.top_statistics.top_source),
        bestConversion: formatSourceOfHireNamedStat(
            data.top_statistics.best_conversion
        ),
    },
});

export const formatJobsOptions = (jobs: IJobLabelOption[]) => {
    return jobs
        .filter((job) => job.status !== JobStatus.Draft)
        .map((job) => ({
            label: job.label.replace(/\s*\(\d+\)$/, ''),
            value: job.value,
        }));
};

export const formatRecruitersOptions = (users: IRecruiterOption[]) => {
    return users.map((user) => ({
        label: `${user.first_name} ${user.last_name}`,
        value: String(user.id),
    }));
};
